import { createComponent, createComputed, createContext, createEffect, createMemo, createSignal, onCleanup, untrack, useContext, type ParentProps } from "solid-js"
import { readUserSessionToken } from "@/storage/user-session"
import {
  createMcpToken,
  getAccountMuxOracle,
  listMcpTokens,
  revokeMcpToken,
  runAccountMuxOracle,
  type AccountMuxOracleState,
  type McpToken,
} from "@/transport/account-mux-oracle"
import { localMode } from "@/transport/local-mode"
import { createQuery, type Query } from "@/utils/query"

/** How long a woken runner may take to report its lease before the sidebar stops showing it starting. */
export const ADOPT_TIMEOUT_MS = 30_000
/** Past a lease's expiry before its state is read again, so the control plane has let it go. */
const LEASE_EXPIRY_GRACE_MS = 2_000

/**
 * The account Mux Oracle on the control plane: its current conversation and
 * runner, and the MCP tokens that reach it. Renders from the last value this
 * account loaded, revalidates when a surface starts observing it, and refreshes
 * on `mux-oracle.updated` instead of polling. A standalone runtime has no
 * account Oracle, so nothing here loads there.
 */

// Disposable, scoped to the signed-in user.
let cachedState: { scope: string; value: AccountMuxOracleState } | undefined

function cachedStateValue() {
  return cachedState?.scope === readUserSessionToken() ? cachedState.value : undefined
}

function createAccountMuxOracle() {
  const [observers, setObservers] = createSignal(0)
  const [tokenObservers, setTokenObservers] = createSignal(0)
  // The machine the last run in this window woke, until a read made after that
  // run (prompted by the runner taking the lease) reports the account's runner.
  const [ran, setRan] = createSignal<string>()
  let runs = 0
  let adoptTimer: ReturnType<typeof setTimeout> | undefined
  const adopted = () => {
    clearTimeout(adoptTimer)
    setRan(undefined)
  }

  // Each read carries the number of runs before it started, so only a read
  // made after the latest run can end that run's adoption. A cached value
  // predates every run.
  const cachedRead = () => {
    const value = cachedStateValue()
    return value && { value, run: -1 }
  }
  const reads = createQuery(
    () => !localMode() && observers() > 0,
    async (_, { signal }) => {
      const run = runs
      const scope = readUserSessionToken()
      const value = await getAccountMuxOracle(signal)
      if (readUserSessionToken() === scope) cachedState = { scope, value }
      return { value, run }
    },
    { initialValue: cachedRead() },
  )
  const state = createMemo(() => reads()?.value)
  // Ends adoption in the same update that shows the read, so the runner never
  // falls back to a guess in between. A read without a runner (the woken
  // machine has not taken the lease yet) keeps it starting.
  createComputed(() => {
    const read = reads()
    if (read && read.run === runs && read.value.runner && untrack(ran)) adopted()
  })
  // A lease that runs out unrenewed publishes nothing: its runner crashed or
  // lost the network. Read the state again once it has expired.
  let leaseTimer: ReturnType<typeof setTimeout> | undefined
  createEffect(() => {
    clearTimeout(leaseTimer)
    const value = state()
    if (!value?.runner || value.leaseRemaining <= 0) return
    const delay = value.leaseRemaining * 1000 + LEASE_EXPIRY_GRACE_MS
    leaseTimer = setTimeout(() => void reads.refresh(), delay)
  })
  const tokens = createQuery(() => !localMode() && tokenObservers() > 0, (_, { signal }) => listMcpTokens(signal))

  // A mutation response replaces local state; a request already in flight
  // predates it, so restart that request rather than let it overwrite.
  const settle = <T,>(query: Query<T>, value: T) => {
    const stale = query.loading || query.error !== undefined
    query.mutate(value)
    if (stale) void query.refresh()
  }

  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  // Latched: a later refresh clears a failed read's error while it runs, and
  // the sidebar must not go back to its placeholder then.
  const [answered, setAnswered] = createSignal(false)
  createComputed(() => {
    if (reads() !== undefined || reads.error !== undefined) setAnswered(true)
  })
  onCleanup(() => {
    clearTimeout(refreshTimer)
    clearTimeout(adoptTimer)
    clearTimeout(leaseTimer)
  })

  return {
    state,
    /** The account has answered once, or failed to: what the sidebar places the Oracle by is known. */
    settled: answered,
    /** The machine running the Oracle's turns now: the lease holder, else the machine this window last woke. */
    runner: () => state()?.runner || ran() || undefined,
    /** The machine a run woke that has not reported taking the lease yet. */
    adopting: ran,
    tokens,
    /** Registers the calling component as showing the Oracle's state. */
    observe() {
      if (untrack(observers) === 0 && untrack(reads) === undefined) reads.mutate(cachedRead())
      setObservers((count) => count + 1)
      onCleanup(() => setObservers((count) => count - 1))
    },
    /** Registers the calling component as showing the MCP tokens. */
    observeTokens() {
      setTokenObservers((count) => count + 1)
      onCleanup(() => setTokenObservers((count) => count - 1))
    },
    /** Picks the runner and wakes it; resolves the machine, or why none can run the Oracle. */
    async run(signal?: AbortSignal) {
      const result = await runAccountMuxOracle(signal)
      // A machine that already holds the lease reports nothing new: renewals
      // publish no event.
      if ("machine" in result && untrack(state)?.runner !== result.machine) {
        runs += 1
        setRan(result.machine)
        clearTimeout(adoptTimer)
        adoptTimer = setTimeout(adopted, ADOPT_TIMEOUT_MS)
      }
      return result
    },
    apply(change?: { runner?: unknown }) {
      // An event that names a runner ends a run's adoption at once: an idle
      // runner takes the lease and lets it go again before a read could see it.
      if (typeof change?.runner === "string" && change.runner && untrack(ran)) adopted()
      // One write publishes one event, but a lease change and a write can land together.
      clearTimeout(refreshTimer)
      refreshTimer = setTimeout(() => void reads.refresh(), 75)
    },
    async createToken(name: string) {
      const created = await createMcpToken(name)
      const { token: _secret, ...listed } = created
      settle(tokens, [...(untrack(tokens) ?? []).filter((item) => item.id !== created.id), listed])
      return created
    },
    async revokeToken(token: McpToken) {
      await revokeMcpToken(token.id)
      settle(tokens, (untrack(tokens) ?? []).filter((item) => item.id !== token.id))
    },
  }
}

export type AccountMuxOracle = ReturnType<typeof createAccountMuxOracle>

const stores = new Set<AccountMuxOracle>()

/**
 * Routes the account stream's `mux-oracle.updated` (with its payload) and
 * reconnects, which may have missed one, to every mounted store.
 */
export function applyAccountMuxOracleChange(change?: { runner?: unknown }) {
  for (const store of stores) store.apply(change)
}

const AccountMuxOracleContext = createContext<AccountMuxOracle>()

export function AccountMuxOracleProvider(props: ParentProps) {
  const store = createAccountMuxOracle()
  stores.add(store)
  onCleanup(() => stores.delete(store))
  return createComponent(AccountMuxOracleContext.Provider, {
    value: store,
    get children() {
      return props.children
    },
  })
}

export function useAccountMuxOracle() {
  const store = useContext(AccountMuxOracleContext)
  if (!store) throw new Error("useAccountMuxOracle must be used within AccountMuxOracleProvider")
  return store
}
